import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import AlertDot from "@/components/ui/AlertDot"

export interface PageSection {
  id: string
  title: string
  // Red dot after the title: the section still needs input.
  dot?: boolean
}

// The nearest scrollable ancestor — AppLayout's content pane, not the window.
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === "auto" || overflowY === "scroll") return node
    node = node.parentElement
  }
  return null
}

// Table of contents for a long page of sections. Each entry links to an
// element with that id (give the target `scroll-mt-5` so it lands below the
// pane padding). The section whose top has scrolled into the upper third of
// the pane is highlighted; at the very bottom the last one is, since a short
// final section may never reach the top.
export default function OnThisPage({ sections, className }: {
  sections: readonly PageSection[]
  className?: string
}) {
  const ref = useRef<HTMLElement>(null)
  const [active, setActive] = useState(sections[0]?.id)

  useEffect(() => {
    const pane = scrollParent(ref.current)
    if (!pane) return

    const update = () => {
      const scrollable = pane.scrollHeight > pane.clientHeight
      const atBottom = pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 1
      if (scrollable && atBottom) {
        setActive(sections[sections.length - 1]?.id)
        return
      }
      const band = pane.getBoundingClientRect().top + pane.clientHeight / 3
      let current = sections[0]?.id
      for (const section of sections) {
        const el = document.getElementById(section.id)
        if (el && el.getBoundingClientRect().top <= band) current = section.id
      }
      setActive(current)
    }

    update()
    pane.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      pane.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [sections])

  return (
    <nav ref={ref} aria-label="On this page" className={cn("rounded-md border bg-card p-3", className)}>
      <p className="mb-2 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">On this page</p>
      <ul className="flex flex-col gap-0.5">
        {sections.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              aria-current={section.id === active ? "location" : undefined}
              className={cn(
                "block border-l-2 px-2 py-1 text-sm",
                section.id === active
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {section.title}
              {section.dot && <AlertDot className="ml-1.5 align-middle" />}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
